import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const FLIP_IN = { rotateY: 90, opacity: 0 };
const FLIP_SHOW = { rotateY: 0, opacity: 1 };
const FLIP_OUT = { rotateY: -90, opacity: 0 };
const FLIP_TIMING = { duration: 0.35 };
import { ArrowLeft, Shuffle, Volume2, Scissors, Package } from "lucide-react";
import { Brand } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { DinnerPlayers } from "@/components/DinnerPlayers";
import { allCategories, pickQuestion } from "@/lib/dinner";
import { CustomQuestions } from "@/components/CustomQuestions";
import { getFamily } from "@/lib/family";
import { speak } from "@/lib/api";

const RULES = ["Devices away, everyone.", "Take turns. No interrupting.", "Everyone answers, grown-ups too!", "Listen, then ask one follow-up question."];

export default function DinnerGame() {
  const [categories, setCategories] = useState(allCategories());
  const [cats, setCats] = useState(categories.map((c) => c.id));
  const onCustomChange = () => {
    const next = allCategories();
    if (next.some((c) => c.id === "custom") && !cats.includes("custom")) setCats([...cats, "custom"]);
    setCategories(next);
  };
  const [players, setPlayers] = useState([]);
  const [turn, setTurn] = useState(0);
  const [card, setCard] = useState(null);
  const [used, setUsed] = useState([]);

  useEffect(() => { getFamily().then((f) => setPlayers(f.profiles.map((p) => p.name))); }, []);

  const toggle = (id) => setCats(cats.includes(id) ? cats.filter((c) => c !== id) : [...cats, id]);
  const draw = () => {
    let next = pickQuestion(cats, used);
    let pool = used;
    if (!next) { pool = []; next = pickQuestion(cats, []); }
    if (!next) return;
    setUsed([...pool, next.q]);
    if (card) setTurn((t) => t + 1);
    setCard(next);
  };
  const who = players.length ? players[turn % players.length] : null;

  return (
    <div className="pp-app mode-kid" data-testid="dinner-game-page">
      <header className="pp-header">
        <Brand />
        <Link to="/manners/table" className="pp-ghost" data-testid="dinner-back-link"><ArrowLeft className="h-4 w-4" /> Table Manners</Link>
      </header>
      <main className="pp-main pp-wide">
        <p className="pp-label">Family dinner ritual</p>
        <h1 className="pp-h1">Dinner Table Questions</h1>
        <p className="pp-sub max-w-xl">A question game that includes everyone. Pick your topics, draw a card, and pass it around the table.</p>
        <ul className="pp-presets mt-4" data-testid="dinner-rules">
          {RULES.map((r) => <li key={r} className="pp-pill">{r}</li>)}
        </ul>
        <div className="pp-dinner-layout">
          <section className="pp-card" data-testid="dinner-setup">
            <DinnerPlayers players={players} setPlayers={setPlayers} />
            <p className="pp-label mt-6">Topics</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button key={c.id} className={`pp-pill ${cats.includes(c.id) ? "on" : ""}`} onClick={() => toggle(c.id)}
                  data-testid={`dinner-topic-${c.id}`}>{c.label}</button>
              ))}
            </div>
            <div className="pp-dinner-print">
              <p className="text-sm opacity-80">No screens at the table? Print the questions as cut-out cards.</p>
              <Link to={`/print/dinner-cards?topics=${cats.join(",")}`} target="_blank"
                className={`pp-ghost ${cats.length ? "" : "pointer-events-none opacity-50"}`} data-testid="dinner-print-cards-link">
                <Scissors className="h-4 w-4" /> Print cut-out cards
              </Link>
              <Link to="/print/dinner-box" target="_blank" className="pp-ghost" data-testid="dinner-print-box-link">
                <Package className="h-4 w-4" /> Print the fold-up card box
              </Link>
            </div>
            <CustomQuestions onChange={onCustomChange} />
          </section>
          <section className="pp-dinner-stage" data-testid="dinner-stage">
            <AnimatePresence mode="wait">
              <motion.div key={card?.q || "start"} className="pp-dinner-card" data-testid="dinner-question-card"
                initial={FLIP_IN} animate={FLIP_SHOW} exit={FLIP_OUT} transition={FLIP_TIMING}>
                {card ? (
                  <>
                    <span className="pp-badge">{card.cat}</span>
                    {who && <p className="pp-dinner-who" data-testid="dinner-current-player">{who}, you're up!</p>}
                    <p className="pp-dinner-q" data-testid="dinner-question-text">{card.q}</p>
                  </>
                ) : (
                  <p className="pp-dinner-q">Ready when you are. Draw the first card!</p>
                )}
              </motion.div>
            </AnimatePresence>
            <div className="pp-actions justify-center">
              {card && (
                <button className="pp-ghost" onClick={() => speak(`${who ? who + ". " : ""}${card.q}`)} data-testid="dinner-read-button">
                  <Volume2 className="h-4 w-4" /> Read aloud
                </button>
              )}
              <button className="pp-primary" onClick={draw} disabled={!cats.length} data-testid="dinner-draw-button">
                <Shuffle className="h-5 w-5" /> {card ? "Next card" : "Draw a card"}
              </button>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
